import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, Loader2, Mic, Minimize2, RefreshCw, Square, X } from "lucide-react";
import { useApi, useAuth, useRealtime, ApiError } from "@ncp/api-client";
import { useLocale } from "@ncp/i18n";
import { Button, cn, toast, useConfirm } from "@ncp/ui";
import { isHello, isModulesRequest, prefetchKey, PROTOCOL_VERSION, type HostDisplay, type HostSession, type PageRoute, type WelcomeMessage } from "@ncp/page-sdk/protocol";
import type { Sensitivity } from "@ncp/page-sdk/desk";
import { HostBridge, type HostActions, type PageSessionInfo } from "./bridge";
import { openTarget, type PageLink } from "./link";
import { frameLoading, moduleCacheAvailable, modulesFor, setModuleBase, warmVendor } from "./moduleCache";

/**
 * Never allow-same-origin: the page runs with an opaque origin, so it cannot read the workspace's storage, cookies or DOM. Nor
 * allow-popups: a window the page opened itself could carry data out in its address, so links out go through the host's open.
 */
export const PAGE_SANDBOX = "allow-scripts allow-forms allow-modals allow-downloads";

const runTokenOf = (runUrl: string) => /\/api\/pages\/run\/([^/]+)\//.exec(runUrl)?.[1] ?? "";
const entryOf = (runUrl: string) => /\/api\/pages\/run\/[^/]+\/([^?#]+)/.exec(runUrl)?.[1] ?? null;

const sameRoute = (a: PageRoute | null | undefined, b: PageRoute | null | undefined) =>
  !!a && !!b && a.path === b.path && JSON.stringify(a.params) === JSON.stringify(b.params);

const SENSITIVITIES: readonly Sensitivity[] = ["public", "internal", "restricted", "confidential"];
const asSensitivity = (v: unknown): Sensitivity | null => (typeof v === "string" && (SENSITIVITIES as readonly string[]).includes(v.toLowerCase()) ? (v.toLowerCase() as Sensitivity) : null);

/** Vendor sets whose module list was asked for once (F2 warm-up), so a second frame does not ask again. */
const vendorMaps = new Set<string>();

// ---- F4 learned prefetch: what a page read for its first view, kept per person, page version, language and entry in this browser.
type PrefetchCall = { capability: string; input?: unknown };
const MAX_PREFETCH = 12;
const LEARN_AFTER_READY_MS = 1000;
const prefetchMemoryKey = (user: string | null, page: string, version: number, locale: string, entry: string | null) =>
  user ? `econic.prefetch.v1:${user}:${page}@${version}:${locale}:${entry ?? ""}` : null;
function readLearned(key: string): PrefetchCall[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((c): c is PrefetchCall => !!c && typeof (c as PrefetchCall).capability === "string").slice(0, MAX_PREFETCH) : [];
  } catch {
    return [];
  }
}
function writeLearned(key: string, calls: PrefetchCall[]): void {
  try {
    if (calls.length) localStorage.setItem(key, JSON.stringify(calls.slice(0, MAX_PREFETCH)));
    else localStorage.removeItem(key);
  } catch {
    // storage full or blocked: nothing to learn this time
  }
}
/** Declared calls first, then learned ones, without repeats, at most 12. */
function mergeCalls(declared: PrefetchCall[], learned: PrefetchCall[]): PrefetchCall[] {
  const out: PrefetchCall[] = [];
  const seen = new Set<string>();
  for (const c of [...declared, ...learned]) {
    const k = prefetchKey(c.capability, c.input);
    if (seen.has(k) || out.length >= MAX_PREFETCH) continue;
    seen.add(k);
    out.push(c.input === undefined ? { capability: c.capability } : { capability: c.capability, input: c.input });
  }
  return out;
}

/** The bottom of whatever the workspace pins over the top of the window above the frame (its sticky header), or 0. */
function pinnedTop(frame: HTMLElement, r: DOMRect): number {
  const x = Math.min(Math.max(r.left + r.width / 2, 0), window.innerWidth - 1);
  for (let e = document.elementFromPoint(x, 1); e && e !== document.body && e !== frame; e = e.parentElement) {
    const position = getComputedStyle(e).position;
    if (position === "sticky" || position === "fixed") return Math.max(0, e.getBoundingClientRect().bottom);
  }
  return 0;
}

export interface PageHostProps {
  pageKey: string;
  /** A version to open instead of the published one (a draft in Page Studio, the private copy behind a suggestion). */
  definitionId?: string | null;
  /** Opens the page in this language instead of the workspace's (Page Studio previews English and Arabic side by side). */
  locale?: "en" | "ar";
  /** A page-kit theme instead of the space's. */
  theme?: string | null;
  /** Changing it opens the page again (after its files changed). */
  reloadKey?: string | number;
  /** Where the page asked to go; by default the workspace's router follows. */
  onOpen?: (target: Record<string, unknown>) => void;
  onReady?: () => void;
  slot?: string;
  space?: string | null;
  matterId?: string | null;
  /** An HTML entry of a multi-entry page; none opens the first. */
  entry?: string | null;
  /** The person followed a link to another of the page's entries inside the frame (null: the first entry). */
  onEntry?: (entry: string | null) => void;
  /** The page's own route (the host URL under /p/:page/…); changes made by back and forward reach the page. */
  route?: PageRoute;
  onRoute?: (route: PageRoute, replace: boolean) => void;
  /** Inline slots: the frame takes the page's height (the kit reports it). */
  autoHeight?: boolean;
  minHeight?: number;
  className?: string;
  onTitle?: (title: string) => void;
  /** What the page shows, for the workspace copilot. */
  onState?: (state: Record<string, unknown>) => void;
  onSession?: (session: PageSessionInfo) => void;
  /** The workspace's handle on the open page (null when it closes): the copilot talks about it and acts in it through this. */
  onLink?: (link: PageLink | null) => void;
  /** False while a kept frame is hidden: it keeps running but cannot navigate, ask or record until it is back in view. */
  active?: boolean;
  /**
   * Drawn over the frame until the page is ready, instead of the "Opening the page…" line: a skeleton of the page's layout. It fills the
   * frame's box (clipped, not clickable) and goes when the page is ready; the error card still replaces it when the page cannot open.
   */
  placeholder?: ReactNode;
  /** Drawn instead of the built-in error card when the page cannot open (the ECONIC desk error card). */
  renderError?: (error: string, retry: () => void) => ReactNode;
  // ---- ECONIC host additions (SPEC C36, shell-ia §15.2–§15.5). All optional: Page Studio and the os/legacy frames pass none of them.
  /** The person's display settings for the frame (HostSession.display, then the `display` event when they change). */
  display?: HostDisplay;
  /** The desk's classification floor (HostSession.classification); the page raises above it with the `classification` op. */
  classification?: Sensitivity | null;
  /** `"host"`: the host draws the page's header (the desk header), so the page hides its own title block (HostSession.chrome). */
  chromeHeader?: "host" | null;
  /** The page raised the banner (`{level, source}`) or dropped its raise (null). */
  onClassification?: (raise: { level: Sensitivity; source: string } | null) => void;
  /** A heartbeat from inside the frame: the person is working there (the host's idle clock counts it). */
  onActivity?: () => void;
  /** F2: the run document asks the host for its vendor modules (the host-fed module cache) instead of fetching them all again. */
  hostModules?: boolean;
  /** F3: the frame is opened ahead of the person (idle warm-up); the session records a warm, and the first view records the open. */
  warm?: boolean;
  /** Immersive mode is drawn below this offset (the classification banner stays above it, shell-ia §4.5). A CSS length. */
  immersiveTop?: string;
  /**
   * F4: remember the read calls the page makes for its first view (per person, page version and language, in this browser) and prefetch
   * them with the declared ones on the next open. The server runs only read-only capabilities in a batch, as the person.
   */
  learnPrefetch?: boolean;
}

interface Recording {
  maxSeconds: number;
  prompt?: string;
  resolve: (r: { data: ArrayBuffer; type: string; durationMs: number }) => void;
  reject: (e: Error) => void;
}

function RecordOverlay({ request, onClose }: { request: Recording; onClose: () => void }) {
  const { locale } = useLocale();
  const [elapsed, setElapsed] = useState(0);
  const stop = useRef<() => void>(() => undefined);
  const cancelled = useRef(false);
  useEffect(() => {
    let stream: MediaStream | null = null;
    let recorder: MediaRecorder | null = null;
    const chunks: Blob[] = [];
    const started = Date.now();
    navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
      stream = s;
      recorder = new MediaRecorder(s);
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream?.getTracks().forEach((t) => t.stop());
        if (cancelled.current) request.reject(new Error(locale === "ar" ? "ألغي التسجيل." : "Recording cancelled."));
        else {
          const blob = new Blob(chunks, { type: recorder!.mimeType || "audio/webm" });
          request.resolve({ data: await blob.arrayBuffer(), type: blob.type, durationMs: Date.now() - started });
        }
        onClose();
      };
      recorder.start(1000);
      stop.current = () => { if (recorder && recorder.state !== "inactive") recorder.stop(); };
    }, (e: unknown) => {
      request.reject(new Error(e instanceof Error ? e.message : "The microphone is not available."));
      onClose();
    });
    const timer = setInterval(() => {
      const s = (Date.now() - started) / 1000;
      setElapsed(s);
      if (s >= request.maxSeconds) stop.current();
    }, 250);
    return () => {
      clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  const mm = `${Math.floor(elapsed / 60)}:${String(Math.floor(elapsed % 60)).padStart(2, "0")}`;
  return (
    <div className="absolute inset-x-0 bottom-6 z-20 mx-auto flex w-[22rem] items-center gap-3 rounded-2xl border border-sand-200 bg-white p-3 shadow-sand-lg" role="dialog" aria-label={locale === "ar" ? "تسجيل صوتي" : "Recording"}>
      <span className="relative grid size-9 place-items-center rounded-full bg-danger-50 text-danger-600"><Mic className="size-4" /><span className="absolute inset-0 animate-ping rounded-full bg-danger-500/20" /></span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{request.prompt ?? (locale === "ar" ? "جار التسجيل…" : "Recording…")}</div>
        <div className="text-xs text-muted-foreground tabular-nums">{mm}</div>
      </div>
      <Button size="sm" onClick={() => stop.current()}><Square className="size-3.5" />{locale === "ar" ? "إيقاف" : "Stop"}</Button>
      <Button size="icon-sm" variant="ghost" aria-label={locale === "ar" ? "إلغاء" : "Cancel"} onClick={() => { cancelled.current = true; stop.current(); }}><X className="size-4" /></Button>
    </div>
  );
}

/**
 * The way out of immersive and presentation mode, at the top centre of the page: "Exit (Esc)" when the mode starts, then only "Esc" after a
 * few seconds, so it covers as little of the page as it can; it opens again under the pointer or keyboard focus. It is placed and coloured
 * inline: the workspace's stylesheet is not built from this package, so a class used only here may be missing from it.
 */
function ExitChip({ ar, buttonRef, onExit }: { ar: boolean; buttonRef: RefObject<HTMLButtonElement | null>; onExit: () => void }) {
  const [fresh, setFresh] = useState(true);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setFresh(false), 4000);
    return () => clearTimeout(timer);
  }, []);
  const full = fresh || near;
  const label = ar ? "خروج (Esc)" : "Exit (Esc)";
  return (
    <button ref={buttonRef} type="button" onClick={onExit} aria-label={label} aria-keyshortcuts="Escape"
      onMouseEnter={() => setNear(true)} onMouseLeave={() => setNear(false)} onFocus={() => setNear(true)} onBlur={() => setNear(false)}
      style={{
        position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 30, color: "#fff",
        background: full ? "rgb(14 15 18 / 0.82)" : "rgb(14 15 18 / 0.5)", boxShadow: "0 8px 20px -6px rgb(0 0 0 / 0.3)", transition: "background-color 200ms",
      }}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
      <Minimize2 className="size-3.5" aria-hidden />{full ? label : "Esc"}
    </button>
  );
}

/**
 * Runs a custom page in a sandboxed frame and is its end of the bridge: opens the page session, answers the page's greeting with a private
 * port, relays its calls with the person's sign-in and the page token, and does what pages may ask of the workspace (toasts, confirms,
 * navigation, files, recording, downloads, immersive and presentation modes, its route in the URL).
 */
export function PageHost({ pageKey, definitionId = null, locale: forcedLocale, theme = null, reloadKey, onOpen, onReady, slot, space, matterId, entry = null, onEntry, route, onRoute, autoHeight = false, minHeight = 120, className, onTitle, onState, onSession, onLink, active = true, placeholder, renderError, display, classification = null, chromeHeader = null, onClassification, onActivity, hostModules = false, warm = false, immersiveTop, learnPrefetch = false }: PageHostProps) {
  const api = useApi();
  const realtime = useRealtime();
  const { user } = useAuth();
  const { locale: workspaceLocale } = useLocale();
  const locale = forcedLocale ?? workspaceLocale;
  const confirm = useConfirm();
  const navigate = useNavigate();
  const frame = useRef<HTMLIFrameElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [session, setSession] = useState<PageSessionInfo | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const [mode, setMode] = useState<"normal" | "immersive" | "presentation">("normal");
  const [recording, setRecording] = useState<Recording | null>(null);
  const [attempt, setAttempt] = useState(0);

  const sessionRef = useRef<PageSessionInfo | null>(null);
  const runTokens = useRef(new Set<string>());
  const welcomed = useRef<string | null>(null);
  const routeFromPage = useRef<PageRoute | null>(null);
  // The entry the frame shows (null: the first one), and the name the first one has, so links between entries inside the frame do not reload it.
  const entryRef = useRef<string | null>(entry);
  const frameEntry = useRef<string | null>(entry);
  const firstEntry = useRef<string | null>(null);
  const pageState = useRef<Record<string, unknown> | null>(null);
  const exitButton = useRef<HTMLButtonElement>(null);
  // The mode the page was last told (in its welcome or a "mode" event).
  const modeSent = useRef<string>("normal");
  // Where focus was when a mode started from outside the frame, to go back to when it ends.
  const focusBefore = useRef<HTMLElement | null>(null);
  // Prefetched read-only results (F4), keyed like page-kit's cache; sent with the welcome, or as the `prefetched` event when they arrive later.
  const prefetched = useRef<Record<string, unknown>>({});
  // F3: a warm frame records its open the first time it is in view.
  const warmOpen = useRef(warm);
  // F4 learned prefetch: the reads of this document's first view, until shortly after it is ready.
  const learning = useRef<{ key: string; calls: (PrefetchCall & { at: number })[]; open: boolean; prefetched: Set<string>; arrived: number } | null>(null);
  const latest = useRef({ route, onRoute, onEntry, onTitle, onState, onSession, onLink, onOpen, onReady, locale, active, mode, display, classification, chromeHeader, onClassification, onActivity });
  latest.current = { route, onRoute, onEntry, onTitle, onState, onSession, onLink, onOpen, onReady, locale, active, mode, display, classification, chromeHeader, onClassification, onActivity };
  const feedModules = hostModules && moduleCacheAvailable();
  if (feedModules) setModuleBase(api.baseUrl);

  const openSession = useCallback(async (renewal = false) => {
    const wanted = entryRef.current;
    const s = await api.post<PageSessionInfo>(`/api/pages/${encodeURIComponent(pageKey)}/session`,
      { space: space ?? null, slot: slot ?? null, matterId: matterId ?? null, entry: wanted, locale, definitionId, theme,
        ...(feedModules ? { hostModules: true } : {}), ...(warmOpen.current && !renewal ? { warm: true } : {}), ...(renewal ? { renewal: true } : {}) });
    runTokens.current.add(runTokenOf(s.runUrl));
    if (wanted === null) firstEntry.current = entryOf(s.runUrl);
    return s;
  }, [api, pageKey, space, slot, matterId, locale, definitionId, theme, feedModules]);

  // An entry asked for from outside (a link, back and forward) opens it; one the frame moved to itself is already showing.
  useEffect(() => {
    entryRef.current = entry;
    if (entry !== frameEntry.current) {
      frameEntry.current = entry;
      setAttempt((a) => a + 1);
    }
  }, [entry]);

  // While this frame loads, the host's module cache does not fetch for itself (the frame has the network first).
  const loadingMark = useRef(false);
  const loadDone = useCallback(() => {
    if (!loadingMark.current) return;
    loadingMark.current = false;
    frameLoading(false);
  }, []);
  useEffect(() => () => loadDone(), [loadDone]);

  useEffect(() => {
    let live = true;
    setReady(false);
    setError(null);
    setHeight(null);
    prefetched.current = {};
    if (feedModules && !loadingMark.current) {
      loadingMark.current = true;
      frameLoading(true);
    }
    openSession().then((s) => {
      if (!live) return;
      sessionRef.current = s;
      setSession(s);
      setSrc(`${api.baseUrl}${s.runUrl}`);
      latest.current.onSession?.(s);
      if (typeof performance !== "undefined") performance.mark(`ncp:page-session:${pageKey}`);
      // F4: the page's declared read-only calls, and the reads it made the last time it opened here, start now, while the frame loads its code.
      const memoryKey = learnPrefetch ? prefetchMemoryKey(user?.id ?? null, s.page.key, s.page.version, latest.current.locale, entryRef.current) : null;
      const calls = mergeCalls(s.prefetch ?? [], memoryKey ? readLearned(memoryKey) : []);
      learning.current = memoryKey ? { key: memoryKey, calls: [], open: true, prefetched: new Set(calls.map((c) => prefetchKey(c.capability, c.input))), arrived: Number.POSITIVE_INFINITY } : null;
      if (calls.length) {
        void api.post<{ results: Array<{ capability: string; input?: unknown; result?: unknown; error?: string | null }> }>(
          "/api/pages/rpc/batch", { calls, locale: latest.current.locale }, { headers: { "X-Ncp-Page-Token": s.pageToken } },
        ).then((r) => {
          if (!live) return;
          const got: Record<string, unknown> = {};
          for (const row of r.results ?? []) if (!row.error && row.result !== undefined) got[prefetchKey(row.capability, row.input)] = row.result;
          if (learning.current) learning.current.arrived = Date.now();
          prefetched.current = { ...prefetched.current, ...got };
          if (Object.keys(got).length && bridge.connected) bridge.emit("prefetched", got);
        }, () => undefined);
      }
      // F2: the first page of a vendor set warms the rest of the set's eager modules at idle, for the next desk.
      if (feedModules && s.vendorSet && !vendorMaps.has(s.vendorSet)) {
        vendorMaps.add(s.vendorSet);
        const set = s.vendorSet;
        // An empty list (the builder could not list the set just now) warms nothing and lets a later page ask again.
        void api.get<{ set: string; files: string[] }>(`/api/pages/vendor-map/${encodeURIComponent(set)}`)
          .then((m) => (m?.files?.length ? warmVendor(set, m.files) : vendorMaps.delete(set)), () => vendorMaps.delete(set));
      }
    }, (e: unknown) => {
      if (!live) return;
      loadDone();
      setError(e instanceof ApiError ? e.problem?.error ?? e.message : String(e));
    });
    return () => { live = false; };
  }, [openSession, attempt, reloadKey]);
  useEffect(() => { if (ready) loadDone(); }, [ready, loadDone]);

  const actions = useMemo<HostActions>(() => ({
    visible: () => latest.current.active,
    ready: () => {
      setReady(true);
      if (typeof performance !== "undefined") performance.mark(`ncp:page-ready:${pageKey}`);
      const l = learning.current;
      if (l?.open) setTimeout(() => {
        if (learning.current !== l || !l.open) return;
        l.open = false;
        // Keep what the page read, and what it was handed and did not have to read again; drop a prefetched read it made anyway after
        // the batch arrived (the page does not use the kit's cache for it), so the next batch does not repeat useless work.
        const made = new Map(l.calls.map((c) => [prefetchKey(c.capability, c.input), c]));
        const keep: PrefetchCall[] = [];
        for (const c of l.calls) if (!(l.prefetched.has(prefetchKey(c.capability, c.input)) && c.at > l.arrived)) keep.push({ capability: c.capability, input: c.input });
        for (const c of readLearned(l.key)) if (l.prefetched.has(prefetchKey(c.capability, c.input)) && !made.has(prefetchKey(c.capability, c.input))) keep.push(c);
        writeLearned(l.key, mergeCalls([], keep));
      }, LEARN_AFTER_READY_MS);
      latest.current.onReady?.();
    },
    activity: () => latest.current.onActivity?.(),
    called: (capability, input) => {
      const l = learning.current;
      if (!l?.open || l.calls.length >= MAX_PREFETCH || typeof capability !== "string" || !/^[a-z][\w.-]{1,79}$/i.test(capability)) return;
      const id = prefetchKey(capability, input);
      if (id.length > 4000 || l.calls.some((c) => prefetchKey(c.capability, c.input) === id)) return;
      l.calls.push({ capability, input, at: Date.now() });
    },
    classification: (args) => {
      const level = asSensitivity(args.level);
      latest.current.onClassification?.(level ? { level, source: String(args.source ?? "").slice(0, 160) } : null);
    },
    toast: ({ message, tone, description }) => {
      const show = tone === "success" ? toast.success : tone === "danger" ? toast.error : tone === "warning" ? toast.warning : toast;
      show(String(message).slice(0, 300), description ? { description: String(description).slice(0, 600) } : undefined);
    },
    confirm: (o) => confirm({
      title: String(o.title).slice(0, 200), description: o.body ? String(o.body).slice(0, 1000) : undefined,
      confirmLabel: o.confirmLabel ?? (latest.current.locale === "ar" ? "تأكيد" : "Confirm"), cancelLabel: o.cancelLabel, tone: o.tone === "danger" ? "destructive" : "default", irreversible: false,
    }),
    open: (target) => (latest.current.onOpen ? latest.current.onOpen(target) : openTarget(navigate, target)),
    title: (title) => latest.current.onTitle?.(title),
    resize: (h) => setHeight(h),
    state: (state) => {
      pageState.current = state;
      latest.current.onState?.(state);
    },
    tools: () => undefined,
    mode: (next) => {
      const m = next === "immersive" || next === "presentation" ? next : "normal";
      setMode(m);
      if (m === "presentation") void container.current?.requestFullscreen?.().catch(() => undefined);
      else if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    },
    route: (r) => {
      const next: PageRoute = { path: (r.path ?? "").replace(/^\/+/, ""), params: r.params ?? {} };
      routeFromPage.current = next;
      latest.current.onRoute?.(next, !!r.replace);
    },
    record: (args) => new Promise((resolve, reject) => setRecording({ maxSeconds: Math.min(600, Math.max(5, args.maxSeconds ?? 120)), prompt: args.prompt, resolve, reject })),
  }), [confirm, navigate, pageKey]);

  const bridge = useMemo(() => new HostBridge(
    api,
    () => sessionRef.current!,
    async () => {
      const renewed = await openSession(true);
      // Only the page token changes; the frame keeps running on its document.
      sessionRef.current = { ...sessionRef.current!, pageToken: renewed.pageToken, expiresAt: renewed.expiresAt };
    },
    actions,
    () => latest.current.locale,
  ), [api, openSession, actions]);

  useEffect(() => () => bridge.detach(), [bridge]);

  useEffect(() => {
    if (!session) return;
    const link: PageLink = {
      pageKey, space: session.space ?? null, name: session.page.name, nameAr: session.page.nameAr ?? null, agents: session.agents,
      open: async () => ({ token: await bridge.freshToken(), state: pageState.current }),
      runTool: (call) => bridge.runTool(call),
      call: (capability, input, signal) => bridge.invoke(capability, input, signal),
      invokeTool: (name, args) => bridge.invokeTool(name, args),
      clientTools: session.clientTools.map((t) => t.name),
      setMode: (next) => actions.mode(next),
    };
    latest.current.onLink?.(link);
    return () => latest.current.onLink?.(null);
  }, [session, bridge, pageKey, actions]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const win = frame.current?.contentWindow;
      const s = sessionRef.current;
      if (!win || e.source !== win || !s) return;
      // F2: the run document asks for its vendor modules before it loads them. Only documents this host served (their run token), once per
      // port; the answer is copies of public code the host fetched itself, never bytes a frame sent.
      if (isModulesRequest(e.data)) {
        const port = e.ports[0];
        if (!port || !e.data.nonce || !runTokens.current.has(e.data.nonce)) return;
        void modulesFor(e.data.urls.map(String)).then((reply) => {
          try { port.postMessage(reply, Object.values(reply.files)); } catch { /* the frame went away */ }
          port.close();
        });
        return;
      }
      if (!isHello(e.data)) return;
      if (e.data.page !== s.page.key || !e.data.nonce || !runTokens.current.has(e.data.nonce) || welcomed.current === e.data.instance) return;
      welcomed.current = e.data.instance;
      const shown = e.data.entry && e.data.entry !== firstEntry.current ? e.data.entry : null;
      const moved = shown !== frameEntry.current;
      if (moved) {
        frameEntry.current = entryRef.current = shown;
        routeFromPage.current = { path: "", params: {} };
        latest.current.onEntry?.(shown);
      }
      const channel = new MessageChannel();
      bridge.attach(channel.port1);
      const hostSession: HostSession = {
        page: { key: s.page.key, version: s.page.version, name: s.page.name, nameAr: s.page.nameAr ?? null, draft: s.draft },
        space: s.space, slot: s.slot, matterId: s.matterId, locale: latest.current.locale, theme: s.theme ?? "ncp", mode: latest.current.mode,
        user: { id: user?.id ?? null, name: (latest.current.locale === "ar" && user?.displayNameAr) || user?.displayName || null },
        capabilities: s.capabilities, grants: s.grants, agents: s.agents, clientTools: s.clientTools,
        route: (moved ? null : latest.current.route) ?? { path: "", params: {} },
        ...(latest.current.display ? { display: latest.current.display } : {}),
        ...(latest.current.classification ? { classification: latest.current.classification } : {}),
        ...(latest.current.chromeHeader ? { chrome: { header: latest.current.chromeHeader } } : {}),
        ...(Object.keys(prefetched.current).length ? { prefetched: prefetched.current } : {}),
      };
      modeSent.current = hostSession.mode;
      const welcome: WelcomeMessage = { ncp: PROTOCOL_VERSION, kind: "welcome", session: hostSession };
      // The frame's origin is opaque ("null"), so "*" is the only target that reaches it; the check above is on the window itself.
      win.postMessage(welcome, "*", [channel.port2]);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [bridge, user]);

  // The accessibility menu reaches open frames at once (shell-ia §9.1, SH-10).
  const displayKey = display ? JSON.stringify(display) : "";
  useEffect(() => {
    if (display && bridge.connected) bridge.emit("display", display);
  }, [displayKey, bridge]);

  // F3: the first time a warmed frame is in view, its open is recorded (the warm-up itself recorded a warm, not an open).
  useEffect(() => {
    if (!active || !warmOpen.current || !session) return;
    warmOpen.current = false;
    void api.post("/api/pages/opened", {}, { headers: { "X-Ncp-Page-Token": session.pageToken } }).catch(() => undefined);
  }, [active, session, api]);

  // Back and forward in the workspace move the page too.
  const routeKey = route ? `${route.path}?${new URLSearchParams(route.params)}` : "";
  useEffect(() => {
    if (!route || !bridge.connected || sameRoute(route, routeFromPage.current)) return;
    routeFromPage.current = route;
    bridge.emit("route", route);
  }, [routeKey, bridge]);

  // A kept frame going out of view (or back) is told, so it can pause its own work; a page in presentation leaves fullscreen.
  useEffect(() => {
    if (!active && document.fullscreenElement && container.current?.contains(document.fullscreenElement)) void document.exitFullscreen().catch(() => undefined);
    if (bridge.connected) bridge.emit("visibility", active ? document.visibilityState : "hidden");
  }, [active, bridge]);

  // An auto-height frame is as tall as its page, so the page is told which part of it is on screen and opens its overlays there.
  useEffect(() => {
    if (!autoHeight || !active) return;
    let raf = 0;
    const send = () => {
      raf = 0;
      const el = frame.current;
      if (!el || !bridge.connected) return;
      const r = el.getBoundingClientRect();
      const from = Math.max(r.top, pinnedTop(el, r));
      const to = Math.min(r.bottom, window.innerHeight);
      bridge.emit("viewport", { top: Math.round(from - r.top), height: Math.max(0, Math.round(to - from)) });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(send); };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const observer = new ResizeObserver(schedule);
    if (frame.current) observer.observe(frame.current);
    schedule();
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [autoHeight, active, bridge, src, ready]);

  // Platform events the page can watch (dataset changes, page.<topic> events); the hub sends only types and identifiers.
  useEffect(() => realtime.on<Record<string, unknown>>("platform.event", (e) => {
    if (bridge.connected) bridge.emit("platform", e);
  }), [realtime, bridge]);

  useEffect(() => {
    const onVisibility = () => bridge.emit("visibility", latest.current.active ? document.visibilityState : "hidden");
    const onFullscreen = () => {
      if (!document.fullscreenElement && mode === "presentation") setMode("normal");
    };
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("fullscreenchange", onFullscreen);
    };
  }, [bridge, mode]);

  // The page hears its mode from the host every time it changes, also when the page asked for it itself, so every part of the page
  // agrees on it (and a page that set its own mode sees the host's Exit, Escape or the end of fullscreen).
  useEffect(() => {
    if (!bridge.connected || modeSent.current === mode) return;
    modeSent.current = mode;
    bridge.emit("mode", mode);
  }, [mode, bridge]);

  // Escape leaves the mode while focus is in the workspace. While it is in the frame, the page has the key: the page kit leaves the mode
  // itself. A mode started from outside the frame (the copilot, the dock) takes focus to the Exit button, as the rest of the window is
  // covered; focus goes back when the mode ends.
  useEffect(() => {
    if (mode === "normal" || !active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      actions.mode("normal");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, active, actions]);

  useEffect(() => {
    if (mode === "normal") return;
    const focused = document.activeElement;
    if (focused && focused !== document.body && focused !== frame.current && !exitButton.current?.contains(focused)) {
      focusBefore.current = focused instanceof HTMLElement ? focused : null;
      exitButton.current?.focus({ preventScroll: true });
    }
    return () => {
      const back = focusBefore.current;
      focusBefore.current = null;
      const lost = !document.activeElement || document.activeElement === document.body || document.activeElement === exitButton.current;
      if (!lost || !latest.current.active) return;
      if (back?.isConnected) back.focus({ preventScroll: true });
      else frame.current?.focus({ preventScroll: true });
    };
  }, [mode === "normal"]);

  const ar = locale === "ar";
  const name = (ar && session?.page.nameAr) || session?.page.name || pageKey;
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  // Immersive mode covers the window except what sits above `immersiveTop` (the classification banner, z 80, shell-ia §4.5). Placed
  // inline: the workspace's stylesheet is not built from this package.
  const immersiveStyle: CSSProperties | undefined = mode === "immersive" && immersiveTop
    ? { position: "fixed", insetInline: 0, top: immersiveTop, bottom: 0, height: "auto", zIndex: 79 }
    : undefined;

  return (
    <div ref={container} style={immersiveStyle}
      className={cn("relative min-h-0", autoHeight ? "w-full" : "h-full w-full", mode === "immersive" && (immersiveTop ? "bg-background" : "fixed inset-0 z-50 h-dvh bg-background"), mode === "presentation" && "bg-background", className)}>
      {error && renderError ? renderError(error, retry) : error ? (
        <div role="alert" className="m-4 flex items-start gap-3 rounded-xl border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{ar ? "تعذر فتح الصفحة" : "The page could not be opened"}</p>
            <p className="mt-0.5 text-danger-700/90">{error}</p>
          </div>
          <Button size="sm" variant="outline" onClick={retry}><RefreshCw className="size-3.5" />{ar ? "إعادة المحاولة" : "Retry"}</Button>
        </div>
      ) : null}
      {session?.buildError ? (
        <div className="mx-4 mt-3 rounded-lg border border-warning-100 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <span className="font-medium">{ar ? "لم يكتمل بناء هذه الصفحة: " : "This page did not build: "}</span>
          <span className="whitespace-pre-wrap font-mono">{session.buildError}</span>
        </div>
      ) : null}
      {src ? (
        <iframe
          key={src}
          ref={frame}
          src={src}
          title={name}
          sandbox={PAGE_SANDBOX}
          allow="clipboard-write; fullscreen"
          referrerPolicy="no-referrer"
          onLoad={() => setTimeout(() => setReady(true), 1500)}
          className={cn("block w-full border-0 bg-transparent", ready ? "opacity-100" : "opacity-0")}
          // The crossfade from the skeleton is 120 ms (shell-ia §15.3; it was 300).
          style={{ transition: "opacity 120ms ease-out", ...(autoHeight ? { height: Math.max(minHeight, height ?? minHeight) } : { height: "100%" }) }}
        />
      ) : null}
      {!ready && !error ? (
        placeholder ? (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-live="polite">{placeholder}</div>
        ) : (
          <div className="pointer-events-none absolute inset-0 grid place-items-center" aria-live="polite">
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin text-aegold-700" />{ar ? "جار فتح الصفحة…" : "Opening the page…"}</span>
          </div>
        )
      ) : null}
      {mode !== "normal" ? <ExitChip ar={ar} buttonRef={exitButton} onExit={() => actions.mode("normal")} /> : null}
      {recording ? <RecordOverlay request={recording} onClose={() => setRecording(null)} /> : null}
    </div>
  );
}
